import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { Truck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem 1.375rem;
  margin-bottom: 1rem;

  h3 {
    margin: 0 0 0.375rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  p.sub { margin: 0 0 0.875rem; font-size: 0.8125rem; color: #64748b; line-height: 1.5; }
`;

const Line = styled.div`
  border: 1px solid ${(p) => (p.$problem ? '#fed7aa' : '#e2e8f0')};
  background: ${(p) => (p.$problem ? '#fffbeb' : '#fff')};
  border-radius: 12px;
  padding: 0.625rem 0.75rem;

  & + & { margin-top: 0.375rem; }

  .top { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
  .what { flex: 1; min-width: 180px; }
  .name { font-weight: 700; color: #0f172a; font-size: 0.875rem; }
  .meta { color: #64748b; font-size: 0.75rem; margin-top: 1px; }
  .for {
    font-size: 0.68rem;
    font-weight: 700;
    border-radius: 999px;
    padding: 0.1rem 0.45rem;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    white-space: nowrap;
  }
  .ordered { font-size: 0.75rem; color: #64748b; white-space: nowrap; font-variant-numeric: tabular-nums; }

  input[type="number"] {
    width: 68px;
    min-height: 44px;
    text-align: center;
    font-size: 1rem;
    font-weight: 700;
    font-family: inherit;
    font-variant-numeric: tabular-nums;
    border: 1.5px solid #cbd5e1;
    border-radius: 10px;
    color: #0f172a;
  }
  input:focus, select:focus, textarea:focus {
    outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15);
  }

  .problem { margin-top: 0.5rem; display: flex; gap: 0.5rem; flex-wrap: wrap; }
  select {
    min-height: 40px;
    font-family: inherit;
    font-size: 0.82rem;
    border: 1.5px solid #cbd5e1;
    border-radius: 9px;
    padding: 0 0.5rem;
    background: #fff;
    color: #334155;
  }
  textarea {
    flex: 1;
    min-width: 200px;
    min-height: 40px;
    font-family: inherit;
    font-size: 0.82rem;
    border: 1.5px solid #cbd5e1;
    border-radius: 9px;
    padding: 0.5rem 0.6rem;
    resize: vertical;
    color: #334155;
  }
  .delta { font-size: 0.72rem; font-weight: 700; color: #b45309; white-space: nowrap; }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.875rem;
  flex-wrap: wrap;

  button {
    font-family: inherit; font-size: 0.875rem; font-weight: 600;
    border-radius: 10px; padding: 0.6rem 1.1rem; min-height: 44px; cursor: pointer;
  }
  .primary { background: #2563eb; border: 1px solid #2563eb; color: #fff; }
  .primary:disabled { opacity: 0.55; cursor: not-allowed; }
  .ghost { background: #fff; border: 1px solid #e2e8f0; color: #334155; }
  .note { font-size: 0.78rem; color: #64748b; }
`;

const Notice = styled.div`
  margin-top: 0.75rem;
  border-radius: 10px;
  padding: 0.6rem 0.8rem;
  font-size: 0.85rem;
  background: ${(p) => (p.$kind === 'error' ? '#fef2f2' : '#f0fdf4')};
  color: ${(p) => (p.$kind === 'error' ? '#991b1b' : '#166534')};
  border: 1px solid ${(p) => (p.$kind === 'error' ? '#fecaca' : '#bbf7d0')};
`;

/**
 * Book in a supplier delivery.
 *
 * Counts default to what was ordered, so a clean delivery is one press. The
 * moment a count differs, a reason selector appears on that line — a bare
 * number tells the next person a box was short but never why.
 *
 * `headless` drops the card's own title and blurb, for when this is the whole
 * tab and the page has already said what the screen is.
 */
const SupplierReceipt = ({ onDone, headless = false }) => {
  const { t } = useTranslation();
  const [lines, setLines] = useState(null);
  const [counts, setCounts] = useState({});
  const [issues, setIssues] = useState({});
  const [notes, setNotes] = useState({});
  const [issueTypes, setIssueTypes] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/stock-transfer-requests/supplier-outstanding', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const rows = data?.lines || [];
      setLines(rows);
      setIssueTypes(data?.issueTypes || {});
      setCounts(Object.fromEntries(rows.map((l) => [l.requestId, String(l.orderedQty)])));
      setIssues({});
      setNotes({});
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Could not load outstanding supplier lines.' });
      setLines([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const payload = lines
        .filter((l) => counts[l.requestId] !== undefined && counts[l.requestId] !== '')
        .map((l) => ({
          requestId: l.requestId,
          receivedQty: parseInt(counts[l.requestId], 10) || 0,
          issue: issues[l.requestId] || undefined,
          note: notes[l.requestId] || undefined,
        }));
      const { data } = await axios.post('/api/stock-transfer-requests/supplier-receipt',
        { lines: payload },
        { headers: { Authorization: `Bearer ${token()}` } });
      const bits = [
        `${data.received} unit(s) booked into central`,
        data.closed ? `${data.closed} refill line(s) closed` : null,
        data.forwarded ? `${data.forwarded} forwarded to stores` : null,
        data.issues?.length ? `${data.issues.length} line(s) flagged` : null,
      ].filter(Boolean);
      setNotice({ kind: 'success', message: bits.join(' · ') });
      await load();
      if (typeof onDone === 'function') onDone(data);
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Could not record the delivery.' });
    } finally {
      setBusy(false);
    }
  };

  if (lines === null) return null;

  if (!lines.length) {
    return (
      <Card>
        {!headless && <h3><Truck size={16} color="#2563eb" /> {t('supplierReceipt.supplierDelivery')}</h3>}
        <p className="sub">{t('supplierReceipt.nothingIsOutstandingWithThe')}</p>
        {notice && <Notice $kind={notice.kind}>{notice.message}</Notice>}
      </Card>
    );
  }

  const totalOrdered = lines.reduce((s, l) => s + l.orderedQty, 0);
  const totalCounted = lines.reduce((s, l) => s + (parseInt(counts[l.requestId], 10) || 0), 0);
  const anyProblem = lines.some((l) => (parseInt(counts[l.requestId], 10) || 0) !== l.orderedQty || issues[l.requestId]);

  return (
    <Card>
      {!headless && (
        <>
          <h3><Truck size={16} color="#2563eb" /> {t('supplierReceipt.supplierDeliveryMarkLensesReceived')}</h3>
          <p className="sub">
            {t('supplierReceipt.countWhatTheSupplierActually')}
          </p>
        </>
      )}

      {lines.map((l) => {
        const counted = parseInt(counts[l.requestId], 10);
        const delta = Number.isFinite(counted) ? counted - l.orderedQty : 0;
        const problem = delta !== 0 || Boolean(issues[l.requestId]);
        return (
          <Line key={l.requestId} $problem={problem}>
            <div className="top">
              <div className="what">
                <div className="name">{l.productName}</div>
                <div className="meta">{l.displayCorrection || t('supplierReceipt.noCorrectionRecorded')}</div>
              </div>
              {l.forStore
                ? <span className="for">{t('supplierReceipt.forStore', { name: l.forStore })}</span>
                : <span className="for" style={{ background: '#f0fdf4', color: '#166534', borderColor: '#bbf7d0' }}>{t('supplierReceipt.centralRestock')}</span>}
              <div className="ordered">{t('supplierReceipt.orderedQty', { count: l.orderedQty })}</div>
              <input
                type="number"
                min="0"
                inputMode="numeric"
                aria-label={t('supplierReceipt.orderedQty', { count: l.orderedQty })}
                value={counts[l.requestId] ?? ''}
                onChange={(e) => setCounts((p) => ({ ...p, [l.requestId]: e.target.value }))}
              />
              {delta < 0 && <span className="delta">{t('supplierReceipt.missingCount', { count: -delta })}</span>}
              {delta > 0 && <span className="delta">{t('supplierReceipt.extraCount', { count: delta })}</span>}
            </div>

            {/* Only asked for when something is off — a reason box on every line
                trains people to ignore it. */}
            {problem && (
              <div className="problem">
                <select
                  aria-label={`Problem type for ${l.productName}`}
                  value={issues[l.requestId] || (delta < 0 ? 'short' : delta > 0 ? 'over' : '')}
                  onChange={(e) => setIssues((p) => ({ ...p, [l.requestId]: e.target.value }))}
                >
                  <option value="">{t('supplierReceipt.reason')}</option>
                  {Object.entries(issueTypes).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <textarea
                  rows={1}
                  placeholder={t('supplierReceipt.whatWasWrongOptionalE')}
                  aria-label={`Notes for ${l.productName}`}
                  value={notes[l.requestId] || ''}
                  onChange={(e) => setNotes((p) => ({ ...p, [l.requestId]: e.target.value }))}
                />
              </div>
            )}
          </Line>
        );
      })}

      <Actions>
        <button type="button" className="primary" onClick={confirm} disabled={busy}>
          {busy ? t('supplierReceipt.recording') : t('supplierReceipt.markReceived')}
        </button>
        <button type="button" className="ghost" onClick={load} disabled={busy}>
          {t('supplierReceipt.reset')}
        </button>
        <span className="note">
          {anyProblem
            ? <><AlertTriangle size={12} style={{ verticalAlign: '-2px' }} /> {totalCounted} of {totalOrdered} {t('supplierReceipt.shortLinesStayOnThe')}</>
            : <><CheckCircle2 size={12} style={{ verticalAlign: '-2px' }} /> {totalCounted} of {totalOrdered} received</>}
        </span>
      </Actions>

      {notice && <Notice $kind={notice.kind}>{notice.message}</Notice>}
    </Card>
  );
};

export default SupplierReceipt;
